import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useMutation } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import ConfirmModal from '../../../common/modal/ConfirmModal';
import MemberRecipientPickerModal from '../MemberRecipientPickerModal';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import { sendMemberPush } from '../../../../api/push';

import {
  ModalContainer,
  ModalHeader,
  ModalHeaderTitle,
  CloseIconButton,
  ModalContentArea,
  ContentRow,
  LeftSection,
  RightSection,
  FormRow,
  FormLabel,
  RecipientCountBox,
  RecipientCountText,
  RecipientEditButton,
  PresetRow,
  PresetChip,
  TitleInput,
  ContentTextArea,
  PreviewWrapper,
  PreviewLabel,
  NotificationCard,
  NotificationHeaderRow,
  AppLogoImg,
  NotificationTextColumn,
  NotificationTitle,
  NotificationBody,
  NotificationPlaceholder,
  PreviewExpandToggle,
  ButtonRow,
} from './MemberPushModalStyled';

const APP_NAME = '바디스위치';

// FCM 메시지 전체 용량(4KB) 안에서 제목/내용이 notification·data 양쪽에 중복으로 실리는 걸
// 감안해도 한참 여유 있고, 기기 배너에서 한 줄/두 줄로 안 잘리는 선으로 잡은 값.
const TITLE_MAX_LENGTH = 30;
const CONTENT_MAX_LENGTH = 100;

// 자주 쓰는 기본 문구 — 눌러서 채우고 필요하면 그대로 수정해서 보낸다.
// 미리보기 상단에 앱 이름(바디스위치)이 이미 따로 표시되므로, 제목은 앱 이름 대신
// 현재 화면의 지점명으로 채워 회원이 어느 지점에서 온 알림인지 바로 알 수 있게 한다.
const buildPushPresets = branchName => [
  {
    label: '이용권 만료 안내',
    title: branchName,
    content: `안녕하세요! 이용 중인 이용권의 만료일이 얼마 남지 않았어요. ${branchName}에 문의해 연장해보세요.`,
  },
  {
    label: '휴면 회원 안내',
    title: branchName,
    content: `오랜만이에요! 다시 운동을 시작해볼까요? ${branchName}에서 회원님을 기다리고 있어요.`,
  },
  {
    label: '이벤트 안내',
    title: branchName,
    content: `${branchName}에서 진행 중인 특별 이벤트를 확인해보세요!`,
  },
];

const MemberPushModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const branchId = user?.branch?.branchId;
  // 헤드 계정 등 소속 지점이 없는 경우엔 지점명 대신 앱 이름으로 폴백
  const branchName = user?.branch?.branchName || APP_NAME;
  const pushPresets = useMemo(() => buildPushPresets(branchName), [branchName]);

  const [recipients, setRecipients] = useState([]);
  const [recipientEditOpen, setRecipientEditOpen] = useState(false);
  const [title, setTitle] = useState(branchName);
  const [content, setContent] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 미리보기 본문 2줄 말줄임 — 실제로 잘렸을 때만 펼치기 화살표를 보여준다
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const previewBodyRef = useRef(null);

  const recipientCount = recipients.length;
  const canSend = recipientCount > 0 && title.trim().length > 0 && content.trim().length > 0;

  useEffect(() => {
    const el = previewBodyRef.current;
    // 펼친 상태에서는 clamp가 풀려 있어 항상 안 잘린 것으로 측정된다 — 접힌 상태 기준으로만 판단한다
    if (!el || previewExpanded) return;
    setPreviewTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [content, previewExpanded]);

  const resetForm = () => {
    setRecipients([]);
    setTitle(branchName);
    setContent('');
    setPreviewExpanded(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const applyPreset = preset => {
    setTitle(preset.title);
    setContent(preset.content);
  };

  const sendMutation = useMutation({
    mutationFn: () =>
      sendMemberPush(
        branchId,
        {
          memberIds: recipients.map(m => m.memberId),
          title,
          content,
        },
        user?.token,
      ),
    onSuccess: ({ totalCount, successCount, failCount }) => {
      setConfirmOpen(false);
      if (failCount === 0) {
        toast.success(t(`${successCount}명에게 알림을 전송했습니다`));
      } else if (successCount === 0) {
        toast.error(t(`전송 실패 (${failCount}/${totalCount})`));
      } else {
        toast.warning(t(`${successCount}명 전송 완료 (${failCount}명 실패)`));
      }
      handleClose();
    },
    onError: () => {
      setConfirmOpen(false);
      toast.error(t('전송에 실패했습니다. 잠시 후 다시 시도해주세요'));
    },
  });

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <ModalHeader>
            <ModalHeaderTitle>{t('회원앱 알림 보내기')}</ModalHeaderTitle>
            <CloseIconButton onClick={handleClose}>
              <CloseIcon style={{ fontSize: '22px' }} />
            </CloseIconButton>
          </ModalHeader>

          <ModalContentArea>
            <ContentRow>
              {/* 왼쪽: 발송 설정 */}
              <LeftSection>
                <FormRow>
                  <FormLabel>{t('받는 회원')}</FormLabel>
                  <RecipientCountBox>
                    <RecipientCountText>
                      {recipientCount}
                      {t('명 선택됨')}
                    </RecipientCountText>
                    <RecipientEditButton onClick={() => setRecipientEditOpen(true)}>
                      {t('회원 선택')}
                    </RecipientEditButton>
                  </RecipientCountBox>
                </FormRow>

                <FormRow>
                  <FormLabel>{t('기본 문구')}</FormLabel>
                  <PresetRow>
                    {pushPresets.map(preset => (
                      <PresetChip key={preset.label} label={t(preset.label)} onClick={() => applyPreset(preset)} />
                    ))}
                  </PresetRow>
                </FormRow>

                <FormRow>
                  <FormLabel>{t('제목')}</FormLabel>
                  <TitleInput
                    fullWidth
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder={t('알림 제목')}
                    inputProps={{ maxLength: TITLE_MAX_LENGTH }}
                    helperText={`${title.length}/${TITLE_MAX_LENGTH}`}
                  />
                </FormRow>

                <FormRow>
                  <FormLabel>{t('내용')}</FormLabel>
                  <ContentTextArea
                    fullWidth
                    multiline
                    rows={8}
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    placeholder={t('알림 내용을 입력해주세요')}
                    inputProps={{ maxLength: CONTENT_MAX_LENGTH }}
                    helperText={`${content.length}/${CONTENT_MAX_LENGTH}`}
                  />
                </FormRow>
              </LeftSection>

              {/* 오른쪽: 회원앱 알림 미리보기 */}
              <RightSection>
                <PreviewWrapper>
                  <PreviewLabel>{t('회원앱 알림 미리보기')}</PreviewLabel>
                  <NotificationCard>
                    <NotificationHeaderRow>
                      <AppLogoImg src="/images/logo/favicon-1.png" alt={APP_NAME} />
                      <NotificationTextColumn>
                        {title ? (
                          <NotificationTitle>{title}</NotificationTitle>
                        ) : (
                          <NotificationPlaceholder>{t('제목을 입력해주세요')}</NotificationPlaceholder>
                        )}
                        {content ? (
                          <NotificationBody ref={previewBodyRef} expanded={previewExpanded}>
                            {content}
                          </NotificationBody>
                        ) : (
                          <NotificationPlaceholder>{t('내용을 입력해주세요')}</NotificationPlaceholder>
                        )}
                      </NotificationTextColumn>
                    </NotificationHeaderRow>
                    {previewTruncated && (
                      <PreviewExpandToggle onClick={() => setPreviewExpanded(prev => !prev)}>
                        <KeyboardArrowDownIcon
                          sx={{
                            fontSize: 18,
                            transform: previewExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 150ms ease',
                          }}
                        />
                      </PreviewExpandToggle>
                    )}
                  </NotificationCard>
                </PreviewWrapper>
              </RightSection>
            </ContentRow>
          </ModalContentArea>

          <ButtonRow>
            <CommonModalButton variant="cancel" onClick={handleClose}>
              {t('취소')}
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={() => setConfirmOpen(true)} disabled={!canSend}>
              {t('보내기')}
            </CommonModalButton>
          </ButtonRow>
        </ModalContainer>
      </Modal>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => sendMutation.mutate()}
        message={`${recipientCount}명에게 알림을 발송하시겠습니까?`}
        highlightText={`${recipientCount}명`}
        highlightColor="#4AB3BC"
        confirmText="발송"
        confirmColor="#4AB3BC"
      />

      <MemberRecipientPickerModal
        open={recipientEditOpen}
        onClose={() => setRecipientEditOpen(false)}
        onApply={setRecipients}
        mode="MEMBER"
        initialRecipients={recipients}
        title="알림 받을 회원 선택"
        showCost={false}
        showGenderFilter={false}
        consentSource="push"
      />
    </>
  );
};

export default MemberPushModal;
